// /reset-password — confirm a password reset using the code from email.
//
// Visual + copy from
//   design/UI : UX v1.0/for-engineering-auth/specs/screen-strings.md § 6.
//
// Flow: user arrives via a reset link with ?email=... in the query string
// (or types email if absent), enters the 6-digit code from their inbox +
// a new password, submits. useAuthFlow.confirmPasswordReset() transitions
// to 'unauthenticated' on success; we render an inline success panel
// before redirecting.
//
// Note: Cognito's confirmForgotPassword needs the email + code + new
// password together. We keep all three on the same screen rather than
// chain a separate "enter your email" step because the email comes in
// the reset link and is usually known.

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  LangToggle,
  PasswordRequirements,
  TextInput,
} from '@gc-coach/design-system';
import { AdminAuthShell, useAuth } from '../../auth/index.js';
import { useI18n } from '../../i18n/index.js';
import styles from './AuthScreens.module.css';

export function PasswordResetScreen() {
  const { t, tt, lang, setLang } = useI18n();
  const { status, error, confirmPasswordReset, clearError } = useAuth();
  const [searchParams] = useSearchParams();

  // Email is usually in the URL (?email=foo@bar). If absent, prompt for it.
  const initialEmail = searchParams.get('email') ?? '';
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [submittedOk, setSubmittedOk] = useState(false);

  const submitting = status.kind === 'submitting-reset';

  // After a successful confirmPasswordReset, useAuthFlow transitions to
  // 'unauthenticated'. We can't easily distinguish "just succeeded" from
  // "user navigated here directly while logged out", so we track a local
  // submittedOk flag set the moment we know the API call succeeded.
  useEffect(() => {
    if (submittedOk && status.kind === 'unauthenticated' && !error) {
      // Successful reset — stay on success panel until user clicks through.
    }
  }, [submittedOk, status.kind, error]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!email || !code || !newPassword || !confirmPassword || submitting) return;
    if (newPassword !== confirmPassword) {
      setLocalError(t('passwordReset.errorMismatch'));
      return;
    }
    try {
      await confirmPasswordReset(email, code, newPassword);
      setSubmittedOk(true);
    } catch {
      // Error already surfaced via useAuth().error — useAuthFlow catches +
      // surfaces. We don't need to do anything here; the Alert below renders.
    }
  };

  function mapErrorToCopy(): string | null {
    if (!error) return null;
    switch (error.code) {
      case 'WRONG_CODE':
        return t('passwordReset.errorCodeInvalid');
      case 'EXPIRED_SESSION':
        return t('passwordReset.errorCodeExpired');
      case 'WEAK_PASSWORD':
        return t('passwordReset.errorWeakPassword');
      default:
        return t('passwordReset.errorGeneric');
    }
  }

  return (
    <AdminAuthShell>
      <div className={styles.topBar}>
          <LangToggle value={lang} onChange={setLang} />
        </div>

        <div className={styles.formColumn}>
          <header className={styles.header}>
            <span className={styles.eyebrow}>{t('passwordReset.eyebrow')}</span>
            <h1 className={styles.title}>
              {submittedOk ? t('passwordReset.successHeadline') : t('passwordReset.headline')}
            </h1>
          </header>

          <Card>
            {submittedOk ? (
              <div className={styles.form}>
                <p className={styles.body}>{t('passwordReset.successBody')}</p>
                <Link to="/login" className={styles.inlineLink}>
                  {t('passwordReset.successButton')}
                </Link>
              </div>
            ) : (
              <form onSubmit={onSubmit} className={styles.form} noValidate>
                {email && (
                  <p className={styles.body}>
                    {tt('passwordReset.bodyTemplate', { email })}
                  </p>
                )}

                {(localError || error) && (
                  <Alert
                    severity="danger"
                    {...(error ? { onDismiss: clearError } : {})}
                  >
                    {localError ?? mapErrorToCopy()}
                  </Alert>
                )}

                {!initialEmail && (
                  <TextInput
                    label={t('forgotPassword.emailLabel')}
                    type="email"
                    name="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.currentTarget.value)}
                    disabled={submitting}
                    required
                  />
                )}

                <TextInput
                  label={t('passwordReset.codeLabel')}
                  hint={t('passwordReset.codeHint')}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  name="code"
                  value={code}
                  onChange={(e) => setCode(e.currentTarget.value.replace(/\D/g, ''))}
                  disabled={submitting}
                  maxLength={6}
                  required
                  autoFocus={!!initialEmail}
                />

                <TextInput
                  label={t('passwordReset.newPasswordLabel')}
                  type="password"
                  name="new-password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.currentTarget.value)}
                  disabled={submitting}
                  revealable
                  required
                />

                <TextInput
                  label={t('passwordReset.confirmPasswordLabel')}
                  type="password"
                  name="confirm-password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.currentTarget.value)}
                  disabled={submitting}
                  revealable
                  required
                />

                <PasswordRequirements value={newPassword} />

                <Button type="submit" kind="brand" fullWidth loading={submitting}>
                  {t('passwordReset.submitButton')}
                </Button>

                <Link to="/login" className={styles.inlineLink}>
                  {t('forgotPassword.backLink')}
                </Link>
              </form>
            )}
          </Card>
        </div>
    </AdminAuthShell>
  );
}
